import type { ExerciseDefinition } from './exercise-generator';

export interface ActivityMenuGroup {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
  readonly order: number;
  readonly exercises: readonly ExerciseDefinition[];
}

interface GroupDefinition {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
  readonly order: number;
}

const GROUPS: readonly GroupDefinition[] = [
  { key: 'infanzia-world', label: 'Scopri il mondo', icon: '🌍', order: 10 },
  { key: 'infanzia-language', label: 'Parole e suoni', icon: '🗣️', order: 20 },
  { key: 'infanzia-body', label: 'Corpo e cura', icon: '🧍', order: 30 },
  { key: 'infanzia-art', label: 'Arte e colori', icon: '🎨', order: 40 },
  { key: 'english', label: 'English', icon: '🇬🇧', order: 50 },
  { key: 'math', label: 'Numeri e matematica', icon: '🔢', order: 60 },
  { key: 'italian', label: 'Italiano', icon: '📚', order: 70 },
  { key: 'attention', label: 'Ascolto e attenzione', icon: '👂', order: 80 },
  { key: 'history', label: 'Storia', icon: '⌛', order: 90 },
  { key: 'geography', label: 'Geografia', icon: '🧭', order: 100 },
  { key: 'science', label: 'Scienze', icon: '🔬', order: 110 },
  { key: 'technology', label: 'Tecnologia', icon: '💻', order: 120 },
  { key: 'music', label: 'Musica', icon: '🎵', order: 130 },
  { key: 'art', label: 'Arte e immagine', icon: '🖼️', order: 140 },
  { key: 'other', label: 'Altri giochi', icon: '🎲', order: 999 }
];

function groupKey(exercise: ExerciseDefinition): string {
  const skill = exercise.skillId.toLowerCase();
  const domain = exercise.domainLabel.toLowerCase();

  if (skill.startsWith('infanzia.world.')) return 'infanzia-world';
  if (skill.startsWith('infanzia.language.')) return 'infanzia-language';
  if (skill.startsWith('infanzia.body.')) return 'infanzia-body';
  if (skill.startsWith('infanzia.art-') || skill.startsWith('infanzia.art.')) return 'infanzia-art';
  if (skill.startsWith('infanzia.english.') || skill.startsWith('english.')) return 'english';
  if (skill.startsWith('math.') || domain.startsWith('numeri') || domain.startsWith('calcolo') || domain.startsWith('geometria') || domain.startsWith('logica')) return 'math';
  if (skill.startsWith('italian.')) return 'italian';
  if (skill.startsWith('school.attention.')) return 'attention';
  if (skill.startsWith('history.') || domain.startsWith('storia')) return 'history';
  if (skill.startsWith('geography.') || domain.startsWith('geografia')) return 'geography';
  if (skill.startsWith('science.') || domain.startsWith('scienze')) return 'science';
  if (skill.startsWith('technology.') || domain.startsWith('tecnologia')) return 'technology';
  if (skill.startsWith('music.') || domain.startsWith('musica')) return 'music';
  if (skill.startsWith('art.') || domain.startsWith('arte')) return 'art';
  return 'other';
}

export function buildActivityMenuGroups(exercises: readonly ExerciseDefinition[]): readonly ActivityMenuGroup[] {
  const buckets = new Map<string, ExerciseDefinition[]>();
  for (const exercise of exercises) {
    const key = groupKey(exercise);
    const bucket = buckets.get(key) ?? [];
    bucket.push(exercise);
    buckets.set(key, bucket);
  }

  return GROUPS
    .filter((definition) => buckets.has(definition.key))
    .map((definition) => ({
      ...definition,
      exercises: buckets.get(definition.key) ?? []
    }));
}

export function exerciseVolumeLabel(exercise: ExerciseDefinition): string {
  if (exercise.items?.length) {
    return `${exercise.items.length} giochi`;
  }
  return `${exercise.variantFloor}+ varianti`;
}
